import { For, Show, createSignal } from "solid-js";
import { Button } from "./Form.tsx";
import { ChevronDownIcon } from "./icons.tsx";
import { useDismiss } from "../lib/popover.ts";
import styles from "./MenuButton.module.scss";

export type MenuItem = {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
};

export type MenuButtonProps = {
  label: string;
  tone?: "primary" | "quiet";
  items: MenuItem[];
};

/**
 * One button standing in for several related actions.
 *
 * A header with three "new X" buttons reads as three decisions when it is one:
 * the reader has already decided to add something and only needs to say what.
 * Folding them into a menu says that, and leaves room in the header for the
 * actions that are not about adding.
 *
 * Not a `<select>`: these are commands, not a value, and a select would report
 * one of them as the current state.
 */
export const MenuButton = (props: MenuButtonProps) => {
  const [open, setOpen] = createSignal(false);
  let root!: HTMLDivElement;

  useDismiss(() => root, () => setOpen(false));

  const choose = (item: MenuItem) => {
    setOpen(false);
    item.onSelect();
  };

  return (
    <div class={styles.menu} ref={root}>
      <Button
        label={props.label}
        tone={props.tone ?? "quiet"}
        icon={<ChevronDownIcon />}
        onClick={() => setOpen(!open())}
      />
      <Show when={open()}>
        <div class={styles.list} role="menu">
          <For each={props.items}>
            {(item) => (
              <button
                class={styles.item}
                type="button"
                role="menuitem"
                disabled={item.disabled === true}
                onClick={() => choose(item)}
              >
                {item.label}
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};
